import { DocumentAccessRole, DocumentEntity, useEditDocument, useIsAuditArchived } from '@/api';
import { InlineEditField, type InlineEditFieldProps } from '@shared/ui';
import { useParams } from 'react-router-dom';

export interface EditableDocumentNameProps extends Pick<
  InlineEditFieldProps,
  | 'size'
  | 'className'
  | 'portal'
  | 'positionOffset'
  | 'widthOffset'
  | 'onEditingChange'
  | 'data-testid'
> {
  id: DocumentEntity['id'];
  name: DocumentEntity['name'];
  /** Read-only role hides the pencil and renders plain text. */
  role?: DocumentAccessRole | string;
  /** Force plain text regardless of role / archive state. */
  readOnly?: boolean;
  /** Called after a successful rename with the new name. */
  onSave?: (name: DocumentEntity['name']) => void;
}

type QueryParams = { id: string };

/**
 * A document's name with the inline rename ({@link InlineEditField}), saved through
 * `useEditDocument`. Read-only roles and archived audits get plain text.
 *
 * Stays in `components/` until `useEditDocument` and `useIsAuditArchived` leave `@/api`
 * for `shared/api` (FRONTEND_MIGRATION campaign 3): `shared` may not import `@/api`, and
 * its callers span `shared/document` and three features, so no single slice can own it.
 */
export default function EditableDocumentName({
  id,
  name,
  role,
  readOnly = false,
  onSave,
  ...fieldProps
}: EditableDocumentNameProps) {
  const { id: auditId } = useParams<QueryParams>();
  const { mutate: editDocument } = useEditDocument();
  const isArchived = useIsAuditArchived();

  const canEdit = !readOnly && !isArchived && role !== 'read_only';

  const handleCommit = (next: string, revert: () => void) => {
    editDocument(
      { data: { name: next }, documentId: String(id), auditId },
      {
        // `onSave` is the server's answer, not the request's: a caller that keeps its
        // own copy of the name (the import wizard's picked files) must never take a
        // name the server refused.
        onSuccess: () => onSave?.(next),
        // A refused rename puts the stored name back; the error toast says why.
        onError: revert,
      },
    );
  };

  return (
    <InlineEditField
      {...fieldProps}
      value={name}
      canEdit={canEdit}
      onCommit={handleCommit}
      testIdPrefix='editable-document-name'
    />
  );
}
